const SCRIPT_ID = 'wizard-recaptcha-script';
const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';

// Bound only how long we wait for the reCAPTCHA script/API to become ready.
// This must NOT cover grecaptcha.execute(): that promise stays pending while
// a human solves an interactive challenge, which can legitimately take much
// longer than a script load.
const SCRIPT_TIMEOUT_MS = 10000;

export const RECAPTCHA_ERROR_MESSAGE =
  "We couldn't verify you're not a robot. Please try again.";

let scriptPromise = null;

export function ensureRecaptchaScript() {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }

    let settled = false;
    let timeoutId = null;
    let script = null;

    const finish = (error) => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (!error) {
        resolve();
        return;
      }
      // Drop the element and the cached promise so a later call retries with
      // a fresh script instead of hanging on a dead one.
      if (script && script.parentNode) script.remove();
      scriptPromise = null;
      reject(error);
    };

    const onReady = () => {
      try {
        window.grecaptcha.ready(() => finish());
      } catch (err) {
        finish(err);
      }
    };

    timeoutId = setTimeout(
      () => finish(new Error('recaptcha script timed out')),
      SCRIPT_TIMEOUT_MS,
    );

    if (window.grecaptcha && window.grecaptcha.ready) {
      onReady();
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      script = existing;
      existing.addEventListener('load', onReady);
      existing.addEventListener('error', () =>
        finish(new Error('recaptcha script failed to load')),
      );
      return;
    }

    script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = onReady;
    script.onerror = () =>
      finish(new Error('recaptcha script failed to load'));
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export function getRecaptchaToken(config, widgetId) {
  if (!config || !config.enabled) return Promise.resolve(null);
  return ensureRecaptchaScript().then(
    () =>
      new Promise((resolve, reject) => {
        window.grecaptcha.ready(() => {
          try {
            if (config.mode === 'checkbox') {
              resolve(window.grecaptcha.getResponse(widgetId) || null);
            } else {
              // Deliberately unbounded: grecaptcha.execute() stays pending
              // while a human solves an interactive challenge, so a fixed
              // timeout here would reject slow (but legitimate) users.
              // Script/API startup is bounded in ensureRecaptchaScript().
              window.grecaptcha.execute(widgetId).then(resolve).catch(reject);
            }
          } catch (err) {
            reject(err);
          }
        });
      }),
  );
}

export function resetRecaptcha(widgetId) {
  if (typeof window === 'undefined' || !window.grecaptcha || widgetId == null) {
    return;
  }
  window.grecaptcha.reset(widgetId);
}
